import type { ExternalTableSchema } from '@packages/table-builder';

export type FilingFilterOption = { label: string; value: string };

const CLASS_TYPE_OPTIONS = [
  { label: 'Common', value: 1 },
  { label: 'Preferred', value: 2 },
];

const DIVIDEND_TYPE_OPTIONS = [
  { label: 'Cumulative', value: 1 },
  { label: 'Non-Cumulative', value: 2 },
];

export function buildCharterStockClassesTable(
  filingOptions: FilingFilterOption[],
): ExternalTableSchema {
  return {
    metadata: {
      title: 'Charter & Stock Classes',
      entity: 'CharterStockClass',
    },

    dataSource: {
      endpoint: '/api/cap-table/api/app/charter-filing/stock-class-list-for-charter',
      method: 'GET',
      rowIdField: 'id',
      confirmBeforeSave: {
        title: 'Save Changes',
        message: 'Are you sure you want to save the changes?',
        variant: 'warning',
      },
      filters: [
        {
          name: 'charterFilingId',
          placeholder: 'Filing Date',
          type: 'select',
          options: filingOptions,
        },
        {
          name: 'classType',
          placeholder: 'Class Type',
          type: 'select',
          options: CLASS_TYPE_OPTIONS,
        },
      ],
    },

    columns: [
      {
        field: 'name',
        header: 'Name',
        sortable: true,
        width: 180,
        visibility: 'locked',
        editable: { type: 'text', placeholder: 'Class name' },
      },
      {
        field: 'classType',
        header: 'Class Type',
        sortable: true,
        width: 130,
        conditionalValues: { '1': 'Common', '2': 'Preferred' },
        editable: {
          type: 'select',
          options: CLASS_TYPE_OPTIONS,
        },
      },
      {
        field: 'preFix',
        header: 'ID Prefix',
        sortable: true,
        width: 110,
        editable: { type: 'text', placeholder: 'Prefix' },
      },
      {
        field: 'authorizedShares',
        header: 'Authorized',
        sortable: true,
        width: 120,
        align: 'right',
        editable: { type: 'number', placeholder: '0' },
      },
      {
        field: 'issuedShares',
        header: 'Issued',
        sortable: true,
        width: 110,
        align: 'right',
      },
      {
        field: 'availableShares',
        header: 'Available',
        sortable: true,
        width: 110,
        align: 'right',
      },
      {
        field: 'votesPerShare',
        header: 'Votes',
        sortable: true,
        width: 100,
        align: 'right',
        editable: { type: 'number', placeholder: '0' },
      },
      {
        field: 'seniority',
        header: 'Seniority',
        sortable: true,
        width: 100,
        align: 'right',
        editable: { type: 'number', placeholder: '0' },
      },
      {
        field: 'liquidationPreferenceMultiple',
        header: 'Liq. Pref.',
        sortable: true,
        width: 110,
        align: 'right',
        visibility: 'picker',
      },
      {
        field: 'conversionRatio',
        header: 'Conversion',
        sortable: true,
        width: 110,
        align: 'right',
        editable: { type: 'number', placeholder: '0' },
      },
      {
        field: 'splitMultiplier',
        header: 'Split',
        sortable: true,
        width: 100,
        align: 'right',
        editable: { type: 'number', placeholder: '1' },
      },
      {
        field: 'parValue',
        header: 'Par Value',
        type: 'text',
        sortable: true,
        width: 120,
        align: 'right',
        emptyDisplay: '-',
        editable: { type: 'number', placeholder: '0' },
      },
      {
        field: 'originalIssuePrice',
        header: 'OIP',
        type: 'text',
        sortable: true,
        width: 120,
        align: 'right',
        emptyDisplay: '-',
        editable: { type: 'number', placeholder: '0' },
      },
      {
        field: 'dividendRate',
        header: 'Dividend %',
        type: 'text',
        sortable: true,
        width: 110,
        align: 'right',
        emptyDisplay: '-',
        editable: { type: 'number', placeholder: '0' },
      },
      {
        field: 'dividendType',
        header: 'Dividend Type',
        sortable: true,
        width: 150,
        conditionalValues: { '1': 'Cumulative', '2': 'Non-Cumulative' },
        editable: {
          type: 'select',
          options: DIVIDEND_TYPE_OPTIONS,
        },
      },
      {
        field: 'documentCount',
        header: 'Documents',
        sortable: true,
        width: 100,
        align: 'center',
        visibility: 'picker',
      },
    ],

    actions: {
      row: [
        { id: 'VIEW_CHARTER_STOCK_CLASS', label: 'View' },
        { id: 'EDIT_CHARTER_STOCK_CLASS', label: 'Edit', actionType: 'startEditing' },
      ],
      toolbar: {
        primary: { id: 'FILE_INITIAL_CHARTER', label: 'File Initial Charter', icon: 'add_circle' },
        secondary: [
          { id: 'AMEND_CHARTER', label: 'Amend Charter', icon: 'edit_document' },
          { id: 'EDIT_CHARTER', label: 'Edit Charter', icon: 'edit' },
          { id: 'RESET_ALL_CHARTERS', label: 'Reset All', icon: 'delete', variant: 'danger' },
          { id: 'IMPORT_CHARTER', label: 'Import Bulk XLS', icon: 'upload' },
          {
            label: 'Downloads',
            icon: 'download',
            type: 'download',
            children: [{ id: 'DOWNLOAD_CHARTER_TEMPLATE', label: 'Download Template' }],
          },
        ],
      },
    },

    features: {
      search: { enabled: true, placeholder: 'Search by name or prefix' },
      pagination: { defaultSize: 10, mode: 'pagination' },
      editing: { bulkSave: true },
      columnPreferences: {
        persistenceKey: 'charterStockClasses',
        reorderable: true,
        resizable: true,
      },
    },
  };
}
